import { useState, useCallback } from "react";
import {
  useLiveEvents,
  DEFAULT_INITIAL_RECORDS_LIVE_EVENTS,
} from "@/hooks/useLiveEvents";
import { useOrg } from "@/hooks/useOrg";
import {
  LiveEventTypeEnum,
  LiveEventAudienceEnum,
  LiveEventStatusEnum,
  LiveEventSortableFieldEnum,
  SortDirectionEnum,
} from "@/__generated__/types";
import { getPageCount } from "@/lib/tableUtils";
import {
  getAudienceLabel,
  getEventTypeLabel,
} from "@/pages/CommunityToolsOverview/GamesLiveEventsTab/utils";
import { useChatChannels } from "@/hooks/useChatChannels";

export const useLiveEventsTable = (status: LiveEventStatusEnum) => {
  const { org } = useOrg();
  const orgId = org?.id;
  const [pageIndex, setPageIndex] = useState(0);
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{
    eventType?: LiveEventTypeEnum[];
    audience?: LiveEventAudienceEnum[];
    chatChannel?: string[];
  }>({});

  const {
    liveEventsData,
    loading,
    hasNextPage,
    hasPreviousPage,
    startCursor,
    endCursor,
    totalCount,
    refetch,
  } = useLiveEvents({
    orgId: orgId || "",
    status: [status],
    first:
      after || (!after && !before)
        ? DEFAULT_INITIAL_RECORDS_LIVE_EVENTS
        : undefined,
    last: before ? DEFAULT_INITIAL_RECORDS_LIVE_EVENTS : undefined,
    after,
    before,
    filters: {
      eventType: selectedFilters.eventType,
      audience: selectedFilters.audience,
      chatChannel: selectedFilters.chatChannel,
    },
    ordering: [
      {
        field: LiveEventSortableFieldEnum.StartTime,
        direction:
          status === LiveEventStatusEnum.Scheduled
            ? SortDirectionEnum.Asc
            : SortDirectionEnum.Desc,
      },
    ],
  });

  const { data: chatChannels, loading: chatChannelsLoading } = useChatChannels({
    orgId,
    hasBeenUsedForLiveEvent: true,
  });

  const handleFilterApply = useCallback(
    (filterId: string, values: string[]) => {
      if (filterId === "eventType") {
        setSelectedFilters((prev) => ({
          ...prev,
          eventType: values.filter(Boolean) as LiveEventTypeEnum[],
        }));
      } else if (filterId === "audience") {
        setSelectedFilters((prev) => ({
          ...prev,
          audience: values.filter(Boolean) as LiveEventAudienceEnum[],
        }));
      } else if (filterId === "chatChannel") {
        setSelectedFilters((prev) => ({ ...prev, chatChannel: values }));
      }
      setPageIndex(0);
      setAfter(null);
      setBefore(null);
    },
    [],
  );

  const getFilterDisplayValue = useCallback(() => {
    const parts: string[] = [];
    if (selectedFilters.eventType?.length) {
      parts.push(
        selectedFilters.eventType.map((et) => getEventTypeLabel(et)).join(", "),
      );
    }
    if (selectedFilters.audience?.length)
      parts.push(
        selectedFilters.audience.map((a) => getAudienceLabel(a)).join(", "),
      );
    if (selectedFilters.chatChannel?.length)
      parts.push(
        selectedFilters.chatChannel
          .map(
            (filterId) =>
              chatChannels?.find(({ id }) => id === filterId)?.displayName,
          )
          .join(", "),
      );
    return parts.join(" · ");
  }, [
    chatChannels,
    selectedFilters.audience,
    selectedFilters.chatChannel,
    selectedFilters.eventType,
  ]);

  const handleNextPage = useCallback(() => {
    setBefore(null);
    setAfter(endCursor || null);
    setPageIndex(pageIndex + 1);
  }, [pageIndex, endCursor]);

  const handlePreviousPage = useCallback(() => {
    setAfter(null);
    setBefore(startCursor || null);
    setPageIndex(pageIndex - 1);
  }, [pageIndex, startCursor]);

  return {
    eventsData: liveEventsData,
    loading: loading || chatChannelsLoading,
    hasNextPage: hasNextPage || false,
    hasPreviousPage: hasPreviousPage || false,
    pageIndex,
    totalCount,
    pageCount: getPageCount(totalCount, DEFAULT_INITIAL_RECORDS_LIVE_EVENTS),
    handleFilterApply,
    getFilterDisplayValue,
    handleNextPage,
    handlePreviousPage,
    availableChatChannels: chatChannels || [],
    refetch,
  };
};
